import {
  centsToMoneyString,
  type DrawerHealthResult,
} from '@fixflow/shared-types';
import { formatCurrency } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';

type Props = {
  health: DrawerHealthResult | null;
  notifyManagers: boolean;
  onNotifyManagersChange: (value: boolean) => void;
  /** When false, hide the notify checkbox (e.g. health ok). */
  showNotify?: boolean;
};

export function DrawerHealthAlerts({
  health,
  notifyManagers,
  onNotifyManagersChange,
  showNotify = true,
}: Props) {
  if (!health || health.severity === 'ok') return null;

  const isCritical = health.severity === 'critical';

  return (
    <div
      className={cn(
        'space-y-3 rounded-xl border p-4 text-sm',
        isCritical ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50',
      )}
    >
      {health.lowChange.length > 0 ? (
        <div>
          <h4
            className={cn(
              'font-black uppercase tracking-wide',
              isCritical ? 'text-red-800' : 'text-amber-900',
            )}
          >
            ⚠ Change needed
          </h4>
          <ul className="mt-2 space-y-1">
            {health.lowChange.map((item) => (
              <li
                key={item.denomination}
                className={cn(
                  'grid grid-cols-[1fr_auto_auto] gap-2 tabular-nums',
                  item.isOut ? 'font-bold text-red-800' : 'text-amber-950',
                )}
              >
                <span>{item.label}</span>
                <span>
                  {item.counted}{' '}
                  <span className="text-slate-500">(need {item.minimum})</span>
                </span>
                <span>
                  {item.isOut ? '← OUT' : `short ${item.shortBy}`}
                </span>
              </li>
            ))}
          </ul>
          {health.suggestedBankOrder.length > 0 ? (
            <div className="mt-3 border-t border-amber-200/80 pt-2">
              <p className="text-xs font-bold uppercase text-amber-900">Suggested bank order</p>
              <ul className="mt-1 space-y-0.5 text-amber-950">
                {health.suggestedBankOrder.map((line) => (
                  <li key={line.denomination}>
                    {line.label}: {line.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {health.exceedsLargeBillThreshold || health.exceedsDropThreshold ? (
        <div className={health.lowChange.length ? 'border-t border-amber-200/80 pt-3' : undefined}>
          <h4 className="font-black uppercase tracking-wide text-amber-950">
            💰 Consider a drop
          </h4>
          <ul className="mt-2 space-y-1 text-amber-950">
            {health.exceedsLargeBillThreshold ? (
              <li>
                $50+ bills: {centsToMoneyString(health.largeBillTotalCents)}{' '}
                <span className="text-slate-600">
                  (threshold {centsToMoneyString(health.largeBillThresholdCents)})
                </span>
              </li>
            ) : null}
            {health.exceedsDropThreshold ? (
              <li>
                Drawer total: {formatCurrency(health.drawerTotalCents / 100)}{' '}
                <span className="text-slate-600">
                  (threshold {centsToMoneyString(health.dropThresholdCents)})
                </span>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {showNotify ? (
        <label className="flex items-center gap-2 border-t border-amber-200/80 pt-3 font-medium text-slate-800">
          <input
            type="checkbox"
            checked={notifyManagers}
            onChange={(e) => onNotifyManagersChange(e.target.checked)}
          />
          Notify manager
        </label>
      ) : null}
    </div>
  );
}
